const MAX_BATCH_SIZE = 34;
const FLUSH_INTERVAL_MS = 5000;
const MAX_TEXT_LENGTH = 233;

let installed = false;
let queue = [];
let flushTimer = null;
let context = {};

const sessionId = (() => {
  const key = 'innerweb_training_telemetry_session';
  try {
    const existing = window.sessionStorage.getItem(key);
    if (existing) return existing;
    const generated = `tr-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
    window.sessionStorage.setItem(key, generated);
    return generated;
  } catch {
    return `tr-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  }
})();

function endpointUrl() {
  const apiUrl = process.env.REACT_APP_API_BASE_URL || '';
  try {
    const url = new URL(apiUrl, window.location.origin);
    url.pathname = url.pathname.replace(/\/webapi\/training\/?$/, '/webapi/ux/track');
    url.search = '';
    url.hash = '';
    return url.toString();
  } catch {
    return '/webapi/ux/track';
  }
}

function deviceType() {
  const width = window.innerWidth || 0;
  if (width <= 767) return 'mobile';
  if (width <= 1199) return 'tablet';
  return 'desktop';
}

function safeText(value) {
  if (value === undefined || value === null) return null;
  return String(value).replace(/\s+/g, ' ').trim().slice(0, MAX_TEXT_LENGTH) || null;
}

/**
 * 🔴 `[sekret-w-telemetrii]` (kontrola MR !34). `t` to token DOSTEPU do panelu stanowiska
 * (`stationAccess.js`, `PARAM_TOKENU`). Bez niego na tej liscie KAZDY klik na kiosku i kazde
 * zadanie stanowiska zapisywalo zywy token jawnym tekstem w `/webapi/ux/track` — trwala kopia
 * poswiadczenia poza paskiem adresu, czytelna dla szerszego kregu niz superadmin, ktory ten
 * token rotuje. Dokladnie to, czego docblock `stationAccess.js` zabrania przy `localStorage`.
 *
 * Nazwa parametru NIE MOZE rozjechac sie z ta lista — pilnuje tego bramka w
 * `RegistrationStation.tokenLeak.test.js`, ktora porownuje `PARAM_TOKENU` z jej zawartoscia.
 */
// `returnUrl` niesie link ustawienia hasła z zaproszenia (`/konto/reset-hasla/<token>`), `badge` — token plakietki
// z `/q/{token}` (kontrola !48 🔴 `[sekret-w-telemetrii]`).
const PARAMETRY_DO_ZAMAZANIA = ['code', 'token', 'password', 'pass', 't', 'returnUrl', 'badge'];

function scrubUrl(rawUrl) {
  if (!rawUrl) return '';
  try {
    const url = new URL(rawUrl, window.location.origin);
    PARAMETRY_DO_ZAMAZANIA.forEach((key) => {
      if (url.searchParams.has(key)) url.searchParams.set(key, '[redacted]');
    });
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    const wzorzec = new RegExp(`([?&](${PARAMETRY_DO_ZAMAZANIA.join('|')})=)[^&]+`, 'gi');
    return String(rawUrl).replace(wzorzec, '$1[redacted]');
  }
}

export const PARAMETRY_ZAMAZYWANE_W_TELEMETRII = PARAMETRY_DO_ZAMAZANIA;

// Kontrola !48: ciała żądań i odpowiedzi axiosa niosą dane gościa (`email` z getForUser, `firstName`/`lastName`,
// powitanie „Cześć, Ewa Nowak!”). Tekst idzie tylko jako długość, gdy klucz jest osobowy albo wartość nie wygląda
// na identyfikator/kod (spacja, `@`) — zostają uuid, liczby, kody błędów (`self_registration_not_allowed`).
// `emai` = numer ewidencyjny ścieżki ad-hoc (`{ [adhocIdType]: value }`, adhoc_type_emai) — jedyny czynnik tożsamości.
const KLUCZ_OSOBOWY = /mail|^emai$|name$|^name|phone|^tel|^login$|pesel|addr|adres/;
// Znaczniki już zamazanych wartości (`[19 zn.]`, `[redacted]`) przechodzą bez ponownego liczenia.
// Pola czysto techniczne przepuszczane wprost (kontrola !48: `userAgent` z `session_start` służy diagnostyce).
const KLUCZE_DIAGNOSTYCZNE = ['userAgent'];
const WARTOSC_TECHNICZNA = /^([A-Za-z0-9_.:-]{1,64}|\[\d+ zn\.\]|\[redacted\])$/;

// Eksport tylko dla testu bramki: telemetria nie moze logowac poswiadczen (token sesji `proof`, `code`, hasla).
export function summarizePayload(payload) {
  if (payload === undefined || payload === null) return null;
  if (typeof payload === 'string') {
    try {
      payload = JSON.parse(payload);
    } catch {
      return WARTOSC_TECHNICZNA.test(payload) ? safeText(payload) : `[${payload.length} zn.]`;
    }
  }
  if (typeof payload !== 'object') return safeText(payload);

  const result = {};
  Object.entries(payload).slice(0, 21).forEach(([key, value]) => {
    const lower = key.toLowerCase();
    if (lower.includes('password') || lower.includes('token') || lower === 'code' || lower.includes('proof')) {
      result[key] = '[redacted]';
    } else if (KLUCZE_DIAGNOSTYCZNE.includes(key) && typeof value === 'string') {
      result[key] = safeText(value);
    } else if (KLUCZ_OSOBOWY.test(lower) && value !== null && value !== undefined && typeof value !== 'object') {
      // Klucz osobowy zamazany niezależnie od typu (`{"phone": 668669328}`).
      result[key] = `[${String(value).length} zn.]`;
    } else if (typeof value === 'string' && !WARTOSC_TECHNICZNA.test(value)) {
      result[key] = `[${value.length} zn.]`;
    } else if (typeof value === 'number' || typeof value === 'boolean') {
      result[key] = value; // liczby i flagi bez zmiany typu (status, durationMs)
    } else if (Array.isArray(value)) {
      result[key] = { type: 'array', length: value.length };
    } else if (value && typeof value === 'object') {
      result[key] = { keys: Object.keys(value).slice(0, 21) };
    } else {
      result[key] = safeText(value);
    }
  });
  return result;
}

// Element z danymi osobowymi (np. wiersz „Nowak Ewa (ew…@gmail.com)” na liście ostatnio zalogowanych) oznaczamy
// `data-track-pii` — etykieta nie trafia do telemetrii, która od MR !48 dochodzi do backendu bez logowania.
function elementLabel(element) {
  if (element.closest && element.closest('[data-track-pii]')) return '[redacted]';
  // `value` jako etykieta tylko dla przycisków (`<input type="submit" value="Wyślij">`) — w polu tekstowym to treść
  // wpisana przez osobę (e-mail, nazwisko).
  const wpisywane = element.tagName === 'TEXTAREA'
    || (element.tagName === 'INPUT' && !['submit', 'button', 'reset', 'checkbox', 'radio'].includes((element.type || '').toLowerCase()));
  return safeText(
    element.getAttribute('aria-label')
    || element.getAttribute('title')
    || element.innerText
    || (wpisywane ? null : element.value)
    || element.name
    || element.id
    || element.tagName
  );
}

function elementId(element) {
  if (element.dataset && element.dataset.trackId) return element.dataset.trackId;
  if (element.id) return `#${element.id}`;
  if (element.name) return `${element.tagName.toLowerCase()}[name="${element.name}"]`;
  const label = elementLabel(element);
  return `${element.tagName.toLowerCase()}${label ? `:${label}` : ''}`.slice(0, 255);
}

// Wpisane wartości (login, e-mail, telefon, imię i nazwisko) to dane osobowe — telemetria dostaje tylko ich długość.
// Wybór z listy (select) i stan pól wyboru zostają, bo to nie treść wpisana przez osobę.
function fieldValue(element) {
  const type = (element.type || '').toLowerCase();
  if (['password', 'hidden'].includes(type)) return '[redacted]';
  if (type === 'checkbox' || type === 'radio') return element.checked ? 'checked' : 'unchecked';
  if (element.tagName === 'SELECT') return safeText(element.value);
  return `[${String(element.value || '').length} zn.]`;
}

function currentPagePath() {
  return scrubUrl(`${window.location.pathname}${window.location.search}${window.location.hash}`);
}

export function setTrainingTelemetryContext(nextContext) {
  context = {
    ...context,
    ...Object.fromEntries(
      Object.entries(nextContext || {}).filter(([, value]) => value !== undefined && value !== null && value !== '')
    ),
  };
}

// `zredagowane`: interceptory axiosa podają metadane już przepuszczone przez `summarizePayload` — drugie przejście
// kasowało ciało i adres zapytania (kontrola !48).
export function trackTrainingEvent(eventType, elementIdValue, elementLabelValue, metadata = {}, { zredagowane = false } = {}) {
  queue.push({
    elementId: elementIdValue || eventType,
    elementLabel: elementLabelValue || null,
    eventType,
    pagePath: currentPagePath(),
    routeName: 'training-reactor',
    deviceType: deviceType(),
    viewportWidth: window.innerWidth || 0,
    viewportHeight: window.innerHeight || 0,
    clientTimestamp: String(Date.now()),
    source: 'training-reactor',
    sessionId,
    trainingId: context.trainingId ? Number(context.trainingId) : undefined,
    userUuid: context.userUuid || undefined,
    // Metadata zdarzeń niosą `message` z getForUser („Cześć, Ewa Nowak!”) i `displayName` — ta sama redakcja co
    // ciała axiosa (kontrola !48). `href` jest już przepuszczony przez `scrubUrl`.
    metadata: {
      ...summarizePayload(context),
      ...(zredagowane ? metadata : summarizePayload(metadata)),
      href: currentPagePath(),
    },
  });

  if (queue.length >= MAX_BATCH_SIZE) {
    flushTrainingTelemetry();
  } else {
    scheduleFlush();
  }
}

function scheduleFlush() {
  if (flushTimer) return;
  flushTimer = window.setTimeout(() => {
    flushTimer = null;
    flushTrainingTelemetry();
  }, FLUSH_INTERVAL_MS);
}

export function flushTrainingTelemetry() {
  if (!queue.length) return;
  const events = queue.splice(0, MAX_BATCH_SIZE);
  const body = JSON.stringify({ events });
  const url = endpointUrl();

  // Backend jest na INNEJ domenie (werken169 vs werken169-training) i odpowiada `Access-Control-Allow-Origin: *`.
  // Beacon z `application/json` wymaga preflightu, a `credentials: 'include'` przy `*` przeglądarka blokuje —
  // E2E WK 08.10: 96 błędów CORS na jedno szkolenie, ani jedno zdarzenie nie doszło. Dlatego: beacon jako
  // `text/plain` (typ bez preflightu; backend czyta surowe JSON-owe ciało), zapasowo fetch BEZ ciasteczek.
  try {
    if (navigator.sendBeacon) {
      const ok = navigator.sendBeacon(url, new Blob([body], { type: 'text/plain;charset=UTF-8' }));
      if (ok) return;
    }
  } catch {
    // Fall through to fetch.
  }

  fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
    body,
    credentials: 'omit',
    keepalive: true,
  }).catch(() => {
    // Telemetry must never break the training flow.
  });
}

export function installTrainingTelemetry(axiosInstance) {
  if (installed) return;
  installed = true;

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element
      ? event.target.closest('button,a,input,select,textarea,[role="button"],[data-track-id]')
      : null;
    if (!target) return;
    trackTrainingEvent('click', elementId(target), elementLabel(target), {
      tag: target.tagName.toLowerCase(),
      type: target.getAttribute('type') || null,
      value: target.matches('input,textarea,select') ? fieldValue(target) : null,
    });
  }, true);

  document.addEventListener('change', (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement || target instanceof HTMLSelectElement)) return;
    trackTrainingEvent('change', elementId(target), elementLabel(target), {
      tag: target.tagName.toLowerCase(),
      type: target.getAttribute('type') || null,
      // Atrybut HTML pola (identyfikator ze źródła) — klucz bez „name”, którego redakcja osobowa by nie przepuściła.
      atrybutPola: target.getAttribute('name') || null,
      value: fieldValue(target),
    });
  }, true);

  document.addEventListener('submit', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    trackTrainingEvent('submit', elementId(target), elementLabel(target), {
      tag: target.tagName.toLowerCase(),
    });
  }, true);

  window.addEventListener('popstate', () => {
    trackTrainingEvent('navigation', 'browser-navigation', 'Browser navigation');
  });

  window.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushTrainingTelemetry();
  });
  window.addEventListener('beforeunload', flushTrainingTelemetry);

  if (axiosInstance && axiosInstance.interceptors) {
    axiosInstance.interceptors.request.use((config) => {
      config.metadata = config.metadata || {};
      config.metadata.telemetryStart = Date.now();
      config.metadata.telemetryId = `req-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
      trackTrainingEvent('http_request', config.metadata.telemetryId, `${(config.method || 'get').toUpperCase()} ${scrubUrl(config.url || '')}`, {
        method: (config.method || 'get').toUpperCase(),
        url: scrubUrl(config.url || ''),
        request: summarizePayload(config.data),
      }, { zredagowane: true });
      return config;
    });

    axiosInstance.interceptors.response.use((response) => {
      const config = response.config || {};
      const started = config.metadata?.telemetryStart || Date.now();
      trackTrainingEvent('http_response', config.metadata?.telemetryId || 'http-response', `${response.status} ${scrubUrl(config.url || '')}`, {
        method: (config.method || 'get').toUpperCase(),
        url: scrubUrl(config.url || ''),
        status: response.status,
        durationMs: Date.now() - started,
        response: summarizePayload(response.data),
      }, { zredagowane: true });
      return response;
    }, (error) => {
      const config = error.config || {};
      const started = config.metadata?.telemetryStart || Date.now();
      trackTrainingEvent('http_error', config.metadata?.telemetryId || 'http-error', `${error.response?.status || 'ERR'} ${scrubUrl(config.url || '')}`, {
        method: (config.method || 'get').toUpperCase(),
        url: scrubUrl(config.url || ''),
        status: error.response?.status || null,
        durationMs: Date.now() - started,
        message: safeText(error.message),
        response: summarizePayload(error.response?.data),
      }, { zredagowane: true });
      return Promise.reject(error);
    });
  }

  trackTrainingEvent('session_start', 'training-session', 'Training session started', {
    userAgent: navigator.userAgent,
    language: navigator.language,
  });
}
